import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScrapReportMaterial } from "src/store/api/generatedApi";
import { useParams, useSearchParams } from "react-router";
import { useIsAdmin } from "hooks/settings";
import {
  generatedApi,
  useGetMissionControlScrapReportQuery,
} from "src/store/api/generatedApi";

type SortField =
  | "material_name"
  | "inventory_tons"
  | "price_per_ton"
  | "open_purchase_order_tons";
type SortDirection = "asc" | "desc";

const cellSx = { whiteSpace: "nowrap" } as const;

const columnSeparatorSx = {
  borderLeft: 1,
  borderLeftColor: "grey.200",
} as const;

const BAR_COLUMN_WIDTH = 200;

const barColumnSx = {
  ...cellSx,
  ...columnSeparatorSx,
  pl: 1,
  width: BAR_COLUMN_WIDTH,
} as const;

const BAR_FLEX = "60%";
const VALUE_FLEX = "40%";
const BAR_HEIGHT = 20;

export const ScrapReportPage = () => {
  const { t } = useTranslation();
  const { tenantGroup } = useParams<{ tenantGroup: string }>();
  const isAdmin = useIsAdmin();

  const { data: tenantsResponse, isLoading: tenantsLoading } =
    generatedApi.endpoints.listMissionControlTenants.useQuery({
      tenantGroupName: tenantGroup,
      visibility: isAdmin ? "all" : "user",
    });

  const tenants = tenantsResponse?.tenants ?? [];
  const selectableTenants = tenants
    .filter((tenant) =>
      tenant.feature_flags.includes("strategy_dashboard_inventory")
    )
    .flatMap((tenant) =>
      tenant.tenant_names.map((name, i) => ({
        tenantName: name,
        label:
          tenant.tenant_names.length > 1
            ? `${tenant.long_name} (${i + 1})`
            : tenant.long_name,
      }))
    );

  const [searchParams] = useSearchParams();
  const [selectedTenantName, setSelectedTenantName] = useState(
    searchParams.get("tenant") ?? undefined
  );
  const activeTenant =
    selectedTenantName ?? selectableTenants[0]?.tenantName ?? "";

  const { data: scrapReport, isFetching: reportLoading } =
    useGetMissionControlScrapReportQuery(
      { tenantName: activeTenant },
      { skip: !activeTenant }
    );

  const [sortField, setSortField] = useState<SortField>("material_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedMaterials = useMemo(() => {
    if (!scrapReport?.materials.length) return [];
    return [...scrapReport.materials].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [scrapReport, sortField, sortDirection]);

  const totals = useMemo(() => {
    if (!scrapReport?.materials.length) return null;
    return computeTotals(scrapReport.materials);
  }, [scrapReport]);

  const maxValues = useMemo(() => {
    if (!scrapReport?.materials.length)
      return { inventory: 0, price: 0, openPOs: 0 };
    const materials = scrapReport.materials;
    return {
      inventory: Math.max(...materials.map((m) => m.inventory_tons ?? 0)),
      price: Math.max(...materials.map((m) => m.price_per_ton ?? 0)),
      openPOs: Math.max(
        ...materials.map((m) => m.open_purchase_order_tons ?? 0)
      ),
    };
  }, [scrapReport]);

  if (tenantsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <FormControl size="small" sx={{ mb: 3, minWidth: 300 }}>
        <InputLabel>{t("selectMill")}</InputLabel>
        <Select
          value={activeTenant}
          label={t("selectMill")}
          onChange={(e) => {
            setSelectedTenantName(e.target.value);
          }}
        >
          {selectableTenants.map(({ tenantName, label }) => (
            <MenuItem key={tenantName} value={tenantName}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {reportLoading ? (
        <ScrapReportSkeleton />
      ) : !scrapReport || scrapReport.materials.length === 0 ? (
        <Typography color="text.secondary">{t("noScrapReportData")}</Typography>
      ) : (
        <TableContainer sx={tableSx}>
          <Table size="small" sx={{ width: "auto" }}>
            <ScrapReportTableHead
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <TableBody>
              {sortedMaterials.map((row) => (
                <TableRow key={row.material_name} hover>
                  <TableCell sx={cellSx}>{row.material_name}</TableCell>
                  <TableCell sx={barColumnSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ width: BAR_FLEX }}>
                        <Box
                          sx={{
                            width: `${maxValues.inventory ? ((row.inventory_tons ?? 0) / maxValues.inventory) * 100 : 0}%`,
                            height: BAR_HEIGHT,
                            bgcolor: "data.blue",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 0.5,
                          }}
                        />
                      </Box>
                      <Box sx={{ width: VALUE_FLEX, textAlign: "right" }}>
                        {row.inventory_tons != null
                          ? row.inventory_tons.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })
                          : "—"}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={barColumnSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ width: BAR_FLEX }}>
                        <Box
                          sx={{
                            width: `${maxValues.price ? ((row.price_per_ton ?? 0) / maxValues.price) * 100 : 0}%`,
                            height: BAR_HEIGHT,
                            bgcolor: "data.green",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 0.5,
                          }}
                        />
                      </Box>
                      <Box sx={{ width: VALUE_FLEX, textAlign: "right" }}>
                        {row.price_per_ton != null
                          ? `$${row.price_per_ton.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={barColumnSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ width: BAR_FLEX }}>
                        <Box
                          sx={{
                            width: `${maxValues.openPOs ? ((row.open_purchase_order_tons ?? 0) / maxValues.openPOs) * 100 : 0}%`,
                            height: BAR_HEIGHT,
                            bgcolor: "data.lightblue",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 0.5,
                          }}
                        />
                      </Box>
                      <Box sx={{ width: VALUE_FLEX, textAlign: "right" }}>
                        {row.open_purchase_order_tons != null
                          ? row.open_purchase_order_tons.toLocaleString(
                              undefined,
                              { maximumFractionDigits: 0 }
                            )
                          : "—"}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {totals != null && (
                <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                  <TableCell sx={cellSx}>{t("total")}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellSx, ...columnSeparatorSx }}
                  >
                    {totals.inventory.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellSx, ...columnSeparatorSx }}
                  >
                    —
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellSx, ...columnSeparatorSx }}
                  >
                    {totals.openPOs.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const SKELETON_ROWS = 8;
const SKELETON_COLUMNS = 4;

const ScrapReportTableHead = ({
  sortField,
  sortDirection,
  onSort,
}: {
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
}) => {
  const { t } = useTranslation();
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={cellSx}>
          <TableSortLabel
            active={sortField === "material_name"}
            direction={sortField === "material_name" ? sortDirection : "asc"}
            onClick={() => onSort?.("material_name")}
          >
            {t("material")}
          </TableSortLabel>
        </TableCell>
        <TableCell align="right" sx={{ ...cellSx, ...columnSeparatorSx }}>
          <TableSortLabel
            active={sortField === "inventory_tons"}
            direction={sortField === "inventory_tons" ? sortDirection : "asc"}
            onClick={() => onSort?.("inventory_tons")}
          >
            {`${t("inventory")} (t)`}
          </TableSortLabel>
        </TableCell>
        <TableCell align="right" sx={{ ...cellSx, ...columnSeparatorSx }}>
          <TableSortLabel
            active={sortField === "price_per_ton"}
            direction={sortField === "price_per_ton" ? sortDirection : "asc"}
            onClick={() => onSort?.("price_per_ton")}
          >
            {`${t("pricePerTon")} ($/t)`}
          </TableSortLabel>
        </TableCell>
        <TableCell align="right" sx={{ ...cellSx, ...columnSeparatorSx }}>
          <TableSortLabel
            active={sortField === "open_purchase_order_tons"}
            direction={
              sortField === "open_purchase_order_tons" ? sortDirection : "asc"
            }
            onClick={() => onSort?.("open_purchase_order_tons")}
          >
            {t("openPurchaseOrders")}
          </TableSortLabel>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

const ScrapReportSkeleton = () => (
  <TableContainer sx={tableSx}>
    <Table size="small" sx={{ width: "auto" }}>
      <ScrapReportTableHead />
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <TableRow key={i}>
            {Array.from({ length: SKELETON_COLUMNS }, (_, col) => (
              <TableCell key={col}>
                <Skeleton variant="text" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const computeTotals = (materials: ScrapReportMaterial[]) => {
  const inventory = materials.reduce(
    (sum, m) => sum + (m.inventory_tons ?? 0),
    0
  );
  const openPOs = materials.reduce(
    (sum, m) => sum + (m.open_purchase_order_tons ?? 0),
    0
  );
  return { inventory, openPOs };
};

const tableSx = {
  width: "fit-content",
  overflow: "hidden",
  border: 1,
  borderColor: "grey.100",
  borderRadius: 1,
  "& .MuiTableHead-root": {
    bgcolor: "grey.50",
  },
  "& .MuiTableCell-head": {
    borderColor: "grey.100",
    color: "text.secondary",
    py: 1,
  },
  "& .MuiTableCell-body": {
    borderColor: "grey.100",
  },
};
